import { ConfigType } from '@scale/shared/defaults';
import { TaskType } from '@scale/shared-types';
import { JsonSchema } from '@ui-schema/ui-schema/JsonSchema';
import {
  schemaPathToValuePath,
  splitDotSeparatedPath,
  valuePathListToSchemaPath,
  valuePathToSchemaPath,
} from 'corp/components/JsonSchemaConfigs/JsonSchemaRenderer/pathHelpers';
import _ from 'lodash';
import { schemaTypeIs } from '@ui-schema/ui-schema/Utils';
import apiFetch from 'react-scale/lib/api-fetch';
import { IConfigMetadata } from '@scale/shared/config/IConfigMetadata';
import * as queryString from 'query-string';
import { ConfigAttributes } from '@scale/shared/types/configAttribute';
import { GetConfigQuery } from '@scale/shared/types/config';

export interface IConfigMeta {
  type: ConfigType;
  id?: string;
  taskType?: TaskType;
}

export function getConfigQuery(
  configMeta: IConfigMeta,
  configAttributes: ConfigAttributes,
  configsPaths?: string,
): GetConfigQuery {
  return { valuesOnly: true, type: configMeta.type, configsPaths, ...configAttributes };
}

export async function fetchConfig(
  configMeta: IConfigMeta,
  configAttributes: ConfigAttributes,
  configsPaths?: string,
) {
  const query = getConfigQuery(configMeta, configAttributes, configsPaths);
  // FIXME (copied from ConfigDashboard): Fetch route includes undefined for projects: /corp-api/lookup/undefined/config
  return await apiFetch<{ config: Record<string, any>; version: number; allMeta: any }>(
    null,
    `corp-api/lookup/${configMeta.id}/config?${queryString.stringify(query)}`,
    { method: 'GET' } as any,
  );
}

export function schemaMetadataToPathMap(schemaMetadata: IConfigMetadata[]) {
  const pathMetadataMap = new Map<string, IConfigMetadata>();
  schemaMetadata.forEach(metadata => pathMetadataMap.set(metadata.path, metadata));
  return pathMetadataMap;
}

export function schemaMetadataToNewPathMap(schemaMetadata: IConfigMetadata[]) {
  const newPathMetadataMap = new Map<string, IConfigMetadata>();
  schemaMetadata.forEach(
    metadata => newPathMetadataMap.set(metadata.newPath || metadata.path, metadata), // If no newPath, just use original path
  );
  return newPathMetadataMap;
}

export async function saveConfig(
  configMeta: IConfigMeta,
  lastVersion: number,
  changes: Record<string, any>,
  configs: Record<string, any>,
  schema: any,
  newPathMetadataMap: Map<string, IConfigMetadata>, // { newPath: metadata }
  configAttributes: ConfigAttributes,
  type: string,
  user: string | undefined,
) {
  const unmappedChanges = unmapChanges(changes, newPathMetadataMap);

  const nestedChanges: Record<string, any> = {};
  for (const valuePath in unmappedChanges) {
    const value = unmappedChanges[valuePath];
    const parsedValue = parseMixedJSON(value, schema, splitDotSeparatedPath(valuePath));
    nestedChanges[valuePath] = parsedValue;
  }

  const query = getConfigQuery(configMeta, configAttributes);
  const { config, version } = await apiFetch(null, `corp-api/lookup/${configMeta.id}/config`, {
    method: 'POST',
    body: {
      lastVersion,
      query,
      changes: nestedChanges,
      attributes: configAttributes,
      type,
      user,
      valuesOnly: true,
    },
  } as any);
  return { config, version };
}

const excludedPaths = [
  'properties.type',
  'properties.key',
  'properties.currentVersion',
  'properties.fieldVersions',
  'properties.changeLog',
];

/**
 * Using the pathMetadataMap, re-arranges the config schema so their paths match the new paths
 * and updates the title, description, and tags
 *
 * @param schema
 * @param pathMetadataMap
 * @param newPathMetadataMap
 */
export const remapConfigSchema = (
  schema: JsonSchema | null,
  pathMetadataMap: Map<string, IConfigMetadata>,
  newPathMetadataMap: Map<string, IConfigMetadata>,
): JsonSchema => {
  if (schema == null) return {};

  // Step 1: Re-arrange configs
  const remappedConfigSchemas = remapConfigSchemaPaths(schema, pathMetadataMap, newPathMetadataMap);

  // Step 2: Overwrite config metadata (title, description, etc.)
  return applyConfigSchemaMetadata(remappedConfigSchemas, pathMetadataMap, newPathMetadataMap);
};

/**
 * Using the pathMetadataMap, re-arranges the config schema so their paths match the new paths
 *
 * @param schema
 * @param pathMetadataMap
 * @param newPathMetadataMap
 * @param schemaPathList - Original schema path for the current property
 * @param newSchemaPathList - Remapped schema path for the current property
 * @param schemaCopy
 */
const remapConfigSchemaPaths = (
  schema: JsonSchema,
  pathMetadataMap: Map<string, IConfigMetadata>,
  newPathMetadataMap: Map<string, IConfigMetadata>,
  schemaPathList: string[] = [],
  newSchemaPathList: string[] = [],
  schemaCopy: JsonSchema = {},
): JsonSchema => {
  const schemaPath = schemaPathList.join('.');
  const currentProperty = schemaPathList.length ? _.get(schema, schemaPath) : schema;

  // hides the property if it's specified as hidden from the UI
  if (currentProperty.hidden) {
    return schemaCopy;
  }

  const metadata: Partial<IConfigMetadata> = pathMetadataMap.get(schemaPath) || {};
  const newSchemaPath = metadata.newPath;

  // If the config has a newPath, it will overwrite its parent path even if its parent is remapped too
  // If it doesn't have a newPath, it will inherit its parent remapped path
  const remappedSchemaPath = newSchemaPath || newSchemaPathList.join('.');
  const remappedSchemaPathList = remappedSchemaPath ? remappedSchemaPath.split('.') : [];
  if (newSchemaPath) {
    const oldKeyPath = [...remappedSchemaPathList, 'oldPath'].join('.');
    _.set(schemaCopy, oldKeyPath, schemaPath);
  }

  // This is a workaround for _.set because when you use it with a numerical key, it will create an array instead of an object
  // For example _.set({}, 'a.1', 'b') === { a: [undefined, b] } when we actually want { a: { 1: b } }
  _.set(schemaCopy, remappedSchemaPath, {});

  Object.entries(currentProperty).forEach(([key, value]) => {
    if (_.isPlainObject(value)) {
      const keyPath = [...schemaPathList, key];
      const newKeyPath = [...remappedSchemaPathList, key];

      remapConfigSchemaPaths(
        schema,
        pathMetadataMap,
        newPathMetadataMap,
        keyPath,
        newKeyPath,
        schemaCopy,
      );
    } else {
      const keyPath = [...remappedSchemaPathList, key].join('.');
      _.set(schemaCopy, keyPath, value);
    }
  });

  return schemaCopy;
};

/**
 * Using the pathMetadataMap, updates the title, descriptions, and tags if they exist for each path
 *
 * @param schema
 * @param pathMetadataMap
 * @param newPathMetadataMap
 * @param schemaPathList
 * @param schemaCopy
 */
const applyConfigSchemaMetadata = (
  schema: JsonSchema,
  pathMetadataMap: Map<string, IConfigMetadata>,
  newPathMetadataMap: Map<string, IConfigMetadata>,
  schemaPathList: string[] = [],
  schemaCopy: JsonSchema = _.cloneDeep(schema),
): JsonSchema => {
  const schemaPath = schemaPathList.join('.');
  const currentProperty = schemaPathList.length ? _.get(schema, schemaPath) : schema;
  const metadata: Partial<IConfigMetadata> =
    newPathMetadataMap.get(schemaPath) || pathMetadataMap.get(schemaPath) || {};

  // Remove hidden and excluded paths
  if (excludedPaths.includes(schemaPath)) return schemaCopy;
  if (currentProperty.hidden) return schemaCopy;

  // This is a hack for remapping configs because if a parent object didn't exist before remapping, it's not valid JSON Schema
  // This ensures that the parent object has the correct type and is JSON Schema compliant
  if ('properties' in currentProperty) {
    _.set(schemaCopy, [...schemaPathList, 'type'], 'object');
  }

  // Step 1: Populate the original property metadata
  Object.entries(currentProperty).forEach(([key, value]) => {
    const nestedPath = [...schemaPathList, key];
    if (_.isPlainObject(value)) {
      applyConfigSchemaMetadata(
        schema,
        pathMetadataMap,
        newPathMetadataMap,
        nestedPath,
        schemaCopy,
      );
    } else {
      _.set(schemaCopy, nestedPath, value);
    }
  });

  // Step 2: Overwrite the original property metadata with what's in the metadata map (if it exists)
  Object.entries(metadata).forEach(([key, value]) => {
    const nestedPath = [...schemaPathList, key];
    _.set(schemaCopy, nestedPath, value);
  });

  return schemaCopy;
};

/**
 * Given nested config values, stringifies JSON Objects and keeps other values the same
 *
 * @param root
 * @param schema
 * @param valuePath
 */
export function stringifyMixedJSON(
  root: any,
  schema: JsonSchema | null,
  valuePath: string[] = [],
): any {
  if (!root) return root;

  const schemaPath = valuePathListToSchemaPath(valuePath);
  const currentSchema = valuePath.length ? _.get(schema, schemaPath) : schema;
  if (
    currentSchema &&
    schemaTypeIs(currentSchema.type, 'string') &&
    currentSchema.widget === 'Code'
  ) {
    return JSON.stringify(root);
  }

  if (Array.isArray(root)) {
    return root.map(property => stringifyMixedJSON(property, schema, [...valuePath, 'items']));
  }

  if (_.isPlainObject(root)) {
    const newProperty: Record<string, any> = {};
    for (const key in root) {
      newProperty[key] = stringifyMixedJSON(root[key], schema, [...valuePath, key]);
    }
    return newProperty;
  }

  return root;
}

/**
 * Given nested config values, parses JSON strings into Objects and keeps other values the same
 *
 * @param root
 * @param schema
 * @param valuePath
 */
export function parseMixedJSON(
  root: any,
  schema: JsonSchema | null,
  valuePath: string[] = [],
): any {
  if (!root) return root;

  const schemaPath = valuePathListToSchemaPath(valuePath);
  const currentSchema = valuePath.length ? _.get(schema, schemaPath) : schema;
  if (
    currentSchema &&
    schemaTypeIs(currentSchema.type, 'string') &&
    currentSchema.widget === 'Code'
  ) {
    try {
      return JSON.parse(root);
    } catch {
      return root; // Return the JSON as a string if parsing fails (invalid JSON)
    }
  }

  if (Array.isArray(root)) {
    return root.map(property => parseMixedJSON(property, schema, [...valuePath, 'items']));
  }

  if (_.isPlainObject(root)) {
    const newProperty: Record<string, any> = {};
    for (const key in root) {
      newProperty[key] = parseMixedJSON(root[key], schema, [...valuePath, key]);
    }
    return newProperty;
  }

  return root;
}

/**
 * Using the pathMetadataMap, re-arranges the config values so their paths match the new paths
 *
 * @param root
 * @param pathMetadataMap
 * @param valuePathList
 * @param schemaPathList
 * @param rootCopy
 */
export const remapConfigValues = (
  root: Record<string, any> | null,
  pathMetadataMap: Map<string, IConfigMetadata>,
  valuePathList: string[] = [],
  schemaPathList: string[] = [],
  rootCopy: Record<string, any> = {},
): Record<string, any> => {
  if (_.isEmpty(root)) return rootCopy;

  const schemaPath = schemaPathList.join('.');
  const valuePath = valuePathList.join('.');
  const currentProperty = valuePathList.length ? _.get(root, valuePath) : root;
  const children = _.keys(currentProperty);

  if (Array.isArray(currentProperty) || !_.isPlainObject(currentProperty) || !children.length) {
    const newSchemaPath = pathMetadataMap.get(schemaPath)?.newPath || '';
    const newValuePath = schemaPathToValuePath(newSchemaPath);
    const newPathDot = newValuePath || valuePath;
    _.set(rootCopy, newPathDot, currentProperty);
    return rootCopy;
  }

  children.forEach(propertyPath => {
    const nestedSchemaPath = [...schemaPathList, 'properties', propertyPath];
    const nestedValuePath = [...valuePathList, propertyPath];
    remapConfigValues(root, pathMetadataMap, nestedValuePath, nestedSchemaPath, rootCopy);
  });
  return rootCopy;
};

/**
 * Unmaps config values back to their actual paths and also parses JSON strings to JSON objects
 *
 * @param root
 * @param newPathMetadataMap
 * @param schema
 * @param valuePathList
 * @param schemaPathList
 * @param rootCopy
 */
export const unmapConfigValues = (
  root: Record<string, any> | null,
  newPathMetadataMap: Map<string, IConfigMetadata>,
  schema: JsonSchema,
  valuePathList: string[] = [],
  schemaPathList: string[] = [],
  rootCopy: Record<string, any> = {},
): Record<string, any> => {
  if (root == null) return rootCopy;

  const schemaPath = schemaPathList.join('.');
  const valuePath = valuePathList.join('.');
  const currentProperty = valuePathList.length ? _.get(root, valuePath) : root;
  const children = _.keys(currentProperty);

  if (Array.isArray(currentProperty) || !_.isPlainObject(currentProperty) || !children.length) {
    const newSchemaPath = newPathMetadataMap.get(schemaPath)?.path || '';
    const newValuePath = schemaPathToValuePath(newSchemaPath);
    const newPathDot = newValuePath || valuePath;
    const parsedValue = parseMixedJSON(currentProperty, schema, valuePathList);
    _.set(rootCopy, newPathDot, _.cloneDeep(parsedValue));
    return rootCopy;
  }

  children.forEach(propertyPath => {
    const nestedSchemaPath = [...schemaPathList, 'properties', propertyPath];
    const nestedValuePath = [...valuePathList, propertyPath];
    unmapConfigValues(
      root,
      newPathMetadataMap,
      schema,
      nestedValuePath,
      nestedSchemaPath,
      rootCopy,
    );
  });
  return rootCopy;
};

/**
 * Unmaps changes back to their actual paths (does not parse JSON strings into objects)
 *
 * @param changes
 * @param newPathMetadataMap
 */
const unmapChanges = (
  changes: Record<string, any> | null,
  newPathMetadataMap: Map<string, IConfigMetadata>, // { newPath: metadata }
): Record<string, any> => {
  if (changes == null) return {};

  const unmappedChanges: Record<string, any> = {};
  _.keys(changes).forEach(valueNewPath => {
    const schemaNewPath = valuePathToSchemaPath(valueNewPath);
    const newPathMetadata = newPathMetadataMap.get(schemaNewPath);
    const schemaPath = newPathMetadata?.path || schemaNewPath; // If no metadata, there is no remapping
    const valuePath = schemaPathToValuePath(schemaPath);
    unmappedChanges[valuePath] = changes[valueNewPath];
  });

  return unmappedChanges;
};
